import { useCallback, useEffect, useRef, useState } from 'react'

type UseScriptStatus = 'idle' | 'loading' | 'ready' | 'error'

type UseScriptOptions = {
  shouldPreventLoad?: boolean
  removeOnUnmount?: boolean
  id?: string
  /** Enable automatic retry on failure. Default: false */
  retry?: boolean
  /** Maximum number of automatic retries. Default: 3 */
  maxRetries?: number
  /** Initial retry delay in ms (doubles with each retry). Default: 1000 */
  initialRetryDelay?: number
}

type UseScriptReturn = {
  status: UseScriptStatus
  /** Current retry attempt (0-based, only relevant when retrying) */
  retryCount: number
  /** Whether currently waiting to retry */
  isRetrying: boolean
  /** Maximum retries configured */
  maxRetries: number
  /** Manually trigger a retry (resets retry count) */
  retry: () => void
}

// Cached script statuses
const cachedScriptStatuses = new Map<string, UseScriptStatus | undefined>()

function getScriptNode(src: string) {
  const node: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`)
  const status = node?.getAttribute('data-status') as UseScriptStatus | undefined

  return {
    node,
    status,
  }
}

/**
 * Hook to load external scripts with optional retry support
 * @param src - Script URL to load
 * @param options - Configuration options
 * @returns Script status and retry controls (when retry is enabled) or just status string (legacy)
 */
export function useScript(src: string | null, options: UseScriptOptions & { retry: true }): UseScriptReturn
export function useScript(src: string | null, options?: UseScriptOptions): UseScriptStatus
export function useScript(src: string | null, options?: UseScriptOptions): UseScriptStatus | UseScriptReturn {
  const retryEnabled = options?.retry ?? false
  const maxRetries = options?.maxRetries ?? 3
  const initialRetryDelay = options?.initialRetryDelay ?? 1000

  const [status, setStatus] = useState<UseScriptStatus>(() => {
    if (!src || options?.shouldPreventLoad) {
      return 'idle'
    }

    if (typeof window === 'undefined') {
      // SSR Handling - always return 'loading'
      return 'loading'
    }

    // Don't use cache when retry is enabled to allow fresh loads
    if (retryEnabled) {
      return 'idle'
    }

    return cachedScriptStatuses.get(src) ?? 'loading'
  })

  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scriptNodeRef = useRef<HTMLScriptElement | null>(null)

  // Load script function for retry-enabled mode
  const loadScript = useCallback(() => {
    if (!src) return

    // Remove existing script if any (for retry scenarios)
    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (existingScript) {
      existingScript.remove()
    }
    cachedScriptStatuses.delete(src)

    setStatus('loading')

    const script = document.createElement('script')
    script.src = src
    script.async = true
    if (options?.id) {
      script.id = options.id
    }
    script.setAttribute('data-status', 'loading')
    scriptNodeRef.current = script

    const handleLoad = () => {
      script.setAttribute('data-status', 'ready')
      setStatus('ready')
      setIsRetrying(false)
      cachedScriptStatuses.set(src, 'ready')
    }

    const handleError = () => {
      script.setAttribute('data-status', 'error')
      setStatus('error')
      setIsRetrying(false)
      // Don't cache error when retry is enabled
      if (!retryEnabled) {
        cachedScriptStatuses.set(src, 'error')
      }
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)

    document.body.appendChild(script)
  }, [src, options?.id, retryEnabled])

  // Manual retry handler
  const manualRetry = useCallback(() => {
    setRetryCount(0)
    setIsRetrying(false)
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    loadScript()
  }, [loadScript])

  // Auto-retry with exponential backoff (only when retry enabled)
  useEffect(() => {
    if (!retryEnabled) return

    if (status === 'error' && retryCount < maxRetries) {
      const delay = initialRetryDelay * Math.pow(2, retryCount)
      setIsRetrying(true)

      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1)
        loadScript()
      }, delay)

      return () => {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current)
        }
      }
    }
  }, [status, retryCount, maxRetries, initialRetryDelay, loadScript, retryEnabled])

  // Initial script load for retry-enabled mode
  useEffect(() => {
    if (!retryEnabled) return
    if (!src || options?.shouldPreventLoad) return

    if (status === 'idle') {
      loadScript()
    }
  }, [status, src, options?.shouldPreventLoad, loadScript, retryEnabled])

  // Cleanup for retry mode
  useEffect(() => {
    if (!retryEnabled) return

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
      if (scriptNodeRef.current && options?.removeOnUnmount) {
        scriptNodeRef.current.remove()
        if (src) cachedScriptStatuses.delete(src)
      }
    }
  }, [retryEnabled, options?.removeOnUnmount, src])

  // Original logic for non-retry mode
  useEffect(() => {
    if (retryEnabled) return // Skip if retry mode is enabled

    if (!src || options?.shouldPreventLoad) {
      return
    }

    const cachedScriptStatus = cachedScriptStatuses.get(src)
    if (cachedScriptStatus === 'ready' || cachedScriptStatus === 'error') {
      // If the script is already cached, set its status immediately
      setStatus(cachedScriptStatus)
      return
    }

    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    const script = getScriptNode(src)
    let scriptNode = script.node

    if (!scriptNode) {
      // Create script element and add it to document body
      scriptNode = document.createElement('script')
      scriptNode.src = src
      scriptNode.async = true
      if (options?.id) {
        scriptNode.id = options.id
      }
      scriptNode.setAttribute('data-status', 'loading')
      document.body.appendChild(scriptNode)

      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event: Event) => {
        const scriptStatus: UseScriptStatus = event.type === 'load' ? 'ready' : 'error'

        scriptNode?.setAttribute('data-status', scriptStatus)
      }

      scriptNode.addEventListener('load', setAttributeFromEvent)
      scriptNode.addEventListener('error', setAttributeFromEvent)
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.status ?? cachedScriptStatus ?? 'loading')
    }

    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event: Event) => {
      const newStatus = event.type === 'load' ? 'ready' : 'error'
      setStatus(newStatus)
      cachedScriptStatuses.set(src, newStatus)
    }

    // Add event listeners
    scriptNode.addEventListener('load', setStateFromEvent)
    scriptNode.addEventListener('error', setStateFromEvent)

    // Remove event listeners on cleanup
    return () => {
      if (scriptNode) {
        scriptNode.removeEventListener('load', setStateFromEvent)
        scriptNode.removeEventListener('error', setStateFromEvent)
      }

      if (scriptNode && options?.removeOnUnmount) {
        scriptNode.remove()
        cachedScriptStatuses.delete(src)
      }
    }
  }, [src, options?.shouldPreventLoad, options?.removeOnUnmount, options?.id, retryEnabled])

  // Return extended object when retry is enabled, otherwise just status for backwards compatibility
  if (retryEnabled) {
    return {
      status,
      retryCount,
      isRetrying,
      maxRetries,
      retry: manualRetry,
    }
  }

  return status
}
